import React, { ReactNode, useCallback, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useUpdateViewMutation } from 'src/fetch/views'
import { TableView } from 'src/models/TableViews/TableView'
import { TableViewManager } from 'src/models/TableViews/TableViewManager'
import { QueryKey } from 'src/utils/queryClient'

import { TableViewConfigContext, TableViewConfigContextValue } from './TableViewConfigContext'

type Props = {
  tableViewManager: TableViewManager
  view: TableView
  children: ReactNode
}

/**
 * Populates `TableViewConfigContext` for the saved-table-view surface.
 * Re-derives whenever `view` changes — saved-table-view users can
 * switch between named views and each `TableView` carries its own
 * `columnManager` / `filterManager`.
 *
 * `onSave` mirrors the prior in-drawer `flushSave` semantics:
 * `updateView` for the active view followed by invalidation of the
 * candidate-custom-columns cache (so newly-promoted candidates refresh).
 */
const SavedTableViewConfigProvider = ({ tableViewManager, view, children }: Props) => {
  const queryClient = useQueryClient()
  const { mutateAsync: updateView } = useUpdateViewMutation()

  const onSave = useCallback(async () => {
    await updateView({
      id: view.id,
      input: tableViewManager.getCreateViewInput(view.databaseProtocol),
    })
    await queryClient.invalidateQueries({ queryKey: [QueryKey.CandidateCustomColumns] })
  }, [queryClient, tableViewManager, updateView, view.databaseProtocol, view.id])

  const value = useMemo<TableViewConfigContextValue>(
    () => ({
      filterManager: view.filterManager,
      columnManager: view.columnManager,
      columnTemplateStore: tableViewManager.columnTemplateStore,
      customizableColumnTemplates: view.customizableColumnTemplates,
      customizableColumnTemplatesByType: view.customizableColumnTemplatesByType,
      hasCustomizableColumnTemplates: view.hasCustomizableColumnTemplates,
      viewType: view.viewType,
      recordTypeId: tableViewManager.recordTypeId ?? null,
      viewDashboardId: tableViewManager.viewDashboardId ?? null,
      onSave,
    }),
    [onSave, tableViewManager, view]
  )

  return <TableViewConfigContext.Provider value={value}>{children}</TableViewConfigContext.Provider>
}

export default React.memo(SavedTableViewConfigProvider)
